import Logo from '@assets/logo.svg';
import Backdrop from '@atoms/Backdrop/Backdrop';
import Drawer from '@atoms/Drawer/Drawer';
import Icon from '@atoms/Icon/Icon';
import Text from '@atoms/Text/Text';
import { ICountry } from '@models/ICountrySelector';
import { IHeader } from '@models/IHeader';
import classNames from 'classnames';
import FocusTrap from 'focus-trap-react';
import { AnimatePresence, useReducedMotion } from 'motion/react';
import { useCallback, useEffect, useState } from 'react';
import { usePreventScroll } from 'react-aria';
import CountrySelector from './CountrySelector';
import HeaderLikeIcon from './HeaderLikeIcon';
import Navigation from './Navigation';
import SearchDropdown from './SearchDropdown';

interface Props extends IHeader {
  readonly animation: boolean;
}

const Header = ({
  animation = true,
  autoCompleteEndpoint,
  countrySelectorLabel,
  countrySelectorPlaceholder,
  featuredContent,
  headerScript,
  homepageLink,
  isRtl,
  languageSelector,
  languageSelectorLabel,
  logoAlt,
  menuTitleLabel,
  mostSearchedTerms,
  mostSearchedTermsLabel,
  navigation,
  quickLinks,
  quickLinksLabel,
  searchPlaceholderLabel,
  searchUrl,
  sharedListPageLink,
  subNav,
}: Props) => {
  const [showMenu, setShowMenu] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [showCountrySelector, setShowCountrySelector] = useState(false);
  const [showBackdrop, setShowBackdrop] = useState(false);
  const shouldReduceMotion = useReducedMotion();
  const [activeCountry, setActiveCountry] = useState<ICountry | undefined>();
  const showAnimations = animation && !shouldReduceMotion;
  const dialogOpen = showMenu || showSearch;

  usePreventScroll({ isDisabled: !dialogOpen });

  const toggleMenu = () => {
    setShowMenu(!showMenu);
    if (!showMenu) {
      window.dataLayer.push({
        event: 'trackingEvent_megamenu_open',
        event_name_variable: 'megamenu_open',
      });
    }
    setShowSearch(false);
    setShowCountrySelector(false);
  };

  const toggleSearch = () => {
    setShowSearch(!showSearch);
    setShowCountrySelector(false);
    setShowMenu(false);
  };

  const toggleCountrySelector = useCallback(() => {
    setShowCountrySelector(!showCountrySelector);
    if (!showCountrySelector) {
      window.dataLayer.push({
        event: 'trackingEvent_navigation_country_selector_open ',
        event_name_variable: 'navigation_country_selector_open ',
      });
    }
    setShowSearch(false);
    setShowMenu(false);
  }, [showCountrySelector]);

  const handleClose = useCallback(() => {
    setShowCountrySelector(false);
    setShowSearch(false);
    setShowMenu(false);
  }, []);

  useEffect(() => {
    // Find the first active country
    const foundCountry = languageSelector.regions
      .flatMap((region) => region.countries)
      .find((country) =>
        country.languages.some((language) => language.isActive),
      );

    // If active country is found, set the name in state
    if (foundCountry) {
      setActiveCountry(foundCountry);
    }
  }, [languageSelector]);

  useEffect(() => {
    setShowBackdrop(dialogOpen);
  }, [dialogOpen]);

  useEffect(() => {
    if (headerScript) {
      const script = document.createElement('script');
      script.setAttribute('type', 'text/javascript');
      const scriptText = document.createTextNode(headerScript);
      script.appendChild(scriptText);
      document.head.appendChild(script);
    }
  }, [headerScript]);

  return (
    <>
      <section
        className={classNames(
          'tw-relative tw-z-50 tw-flex tw-h-[56px] tw-w-full tw-border-b tw-bg-white tw-py-3 tw-text-grey-100 tw-transition-transform tw-duration-300 tw-ease-in-out md:tw-h-2xl dark:tw-bg-grey-10',
          showMenu || showSearch ? 'tw-border-grey-20' : 'tw-border-grey-10',
          showCountrySelector ? 'tw-z-50 md:tw-z-20' : 'tw-z-50',
        )}
        id="header"
      >
        <div className="tw-container tw-flex tw-w-full tw-flex-row">
          <div className="tw-grid tw-w-full tw-grid-cols-5 tw-gap-3xs md:tw-grid-cols-3 md:tw-gap-xs">
            <div className="tw-flex tw-items-center">
              <button
                type="button"
                className="tw-flex tw-flex-row tw-items-center tw-gap-2.5"
                onClick={toggleMenu}
                aria-label={showMenu ? 'Close Menu' : 'Open Menu'} // TODO: Get label
              >
                <Icon
                  name={showMenu ? 'cross' : 'menu'}
                  className="tw-text-2xl"
                  size="1.5rem"
                />
                <Text
                  variant="body-small"
                  className="tw-hidden tw-text-sm tw-font-semibold md:tw-inline-block"
                >
                  {menuTitleLabel}
                </Text>
              </button>
            </div>
            <div className="tw-col-span-3 tw-flex tw-items-center tw-justify-center md:tw-col-span-1">
              <a
                className="tw-h-[28px] tw-w-[112px] md:tw-h-lg md:tw-w-[140px]"
                href={homepageLink || '/'}
                aria-label={logoAlt}
              >
                <Logo />
              </a>
            </div>
            <div className="tw-flex tw-items-center tw-justify-end">
              <div className="tw-grid tw-auto-cols-max tw-grid-flow-col tw-gap-2.5">
                <button
                  type="button"
                  onClick={toggleCountrySelector}
                  aria-label="search"
                  className="tw-hidden md:tw-inline-block"
                >
                  {activeCountry?.image ? (
                    <img
                      src={activeCountry.image}
                      alt={activeCountry.name}
                      className="tw-h-sm tw-w-sm tw-rounded-full tw-object-cover"
                    />
                  ) : (
                    <Icon name="globe" className="tw-text-2xl" size="1.5rem" />
                  )}
                </button>
                <HeaderLikeIcon link={sharedListPageLink} />
                <button
                  type="button"
                  onClick={toggleSearch}
                  aria-label="search"
                >
                  <Icon
                    name="magnifier"
                    className="tw-text-2xl"
                    size="1.5rem"
                  />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
      <AnimatePresence>
        {showMenu && (
          <FocusTrap
            focusTrapOptions={{
              initialFocus: false,
              preventScroll: true,
              allowOutsideClick: true,
            }}
          >
            <Navigation
              showAnimations={showAnimations}
              activeCountry={activeCountry || undefined}
              navigation={navigation}
              subNav={subNav}
              featuredContent={featuredContent}
              quickLinks={quickLinks}
              countrySelectCallback={toggleCountrySelector}
            />
          </FocusTrap>
        )}
        {showSearch && (
          <FocusTrap
            focusTrapOptions={{
              preventScroll: true,
              allowOutsideClick: true,
            }}
          >
            <SearchDropdown
              autoCompleteEndpoint={autoCompleteEndpoint}
              mostSearchedTerms={mostSearchedTerms}
              mostSearchedTermsLabel={mostSearchedTermsLabel}
              quickLinks={quickLinks}
              quickLinksLabel={quickLinksLabel}
              searchPlaceholderLabel={searchPlaceholderLabel}
              searchUrl={searchUrl}
              showAnimations={showAnimations}
              onCloseCallback={() => {
                setShowSearch(false);
              }}
            />
          </FocusTrap>
        )}
      </AnimatePresence>
      <Drawer
        showAnimations={showAnimations}
        isRtl={isRtl}
        isOpen={showCountrySelector}
        setIsOpen={setShowCountrySelector}
        showCloseButton
      >
        <CountrySelector
          activeCountry={activeCountry || undefined}
          countrySelectorLabel={countrySelectorLabel}
          countrySelectorPlaceholder={countrySelectorPlaceholder}
          languageSelectorLabel={languageSelectorLabel}
          languageSelector={languageSelector}
        />
      </Drawer>
      <AnimatePresence>
        {showBackdrop && <Backdrop onCloseCallback={handleClose} />}
      </AnimatePresence>
    </>
  );
};

export default Header;
